import styles from "./Filters.module.css";
import { TextField, Button, InputAdornment, FormControlLabel, Checkbox } from "@mui/material";
import Icon from "@mdi/react";
import { mdiFilterOff, mdiMagnify } from "@mdi/js";
import AnimatedNumber from "animated-number-react";

// react
import React, { useState, useEffect } from "react";
import useDebounce from "hooks/useDebounce";

// context
import { useContext } from "react";
import { MyContext } from "components/RoleContext";
import { Role } from "tools/Enums";

// tools
import { Published, Status } from "tools/Enums";
import { dropDownModuleOptionsArray } from "tools/getModule";
import { dropDownStatusOptionsArray } from "tools/getStatus";
import { dropDownPriorityOptionsArray } from "tools/getPriority";
import { dropDownPublicationOptionsArray } from "tools/getPublication";

// components
import DropDownMenu, { dropDownOptions } from "components/DropDownMenu";

export default function Filters({ features, onChange, filteredFeaturesLength }) {
	const { role } = useContext(MyContext);
	const [filter, setFilter] = useState({
		module: -1,
		priority: -1,
		published: -1,
		status: -1,
		voted: false,
		requestedByMe: false,
		searchField: "",
	});
	const [estDevTimeFeaturesFiltered, setEstDevTimeFeaturesFiltered] = useState(
		Math.round(features.reduce((n, { estDevTimeInDays }) => n + estDevTimeInDays, 0))
	);
	const debouncedSearchField = useDebounce(filter.searchField, 500);
	const LOCAL_STORAGE_FEATURES_FILTER = "featuresFilter";

	const saveFilterInLocalStorage = (filter) => {
		localStorage.setItem(LOCAL_STORAGE_FEATURES_FILTER, JSON.stringify(filter));
	};
	const loadFilter = () => {
		let localFilter = localStorage.getItem(LOCAL_STORAGE_FEATURES_FILTER);
		if (localFilter != null) setFilter(JSON.parse(localStorage.getItem(LOCAL_STORAGE_FEATURES_FILTER)));
	};

	useEffect(() => {
		loadFilter();
	}, []);

	// Changes features available everytime filter changes and pass it to parent
	useEffect(() => {
		// Initialized filtered features to all features
		let filteredFeatures = features;

		// Filter module
		if (filter.module !== -1)
			filteredFeatures = filteredFeatures.filter((feature) => feature.module === filter.module);

		// Filter priority
		if (filter.priority !== -1)
			filteredFeatures = filteredFeatures.filter((feature) => feature.priority === filter.priority);

		// Filter published
		if (filter.published !== -1) {
			let published = filter.published === Published.Visible; // cast filter.published to boolean
			filteredFeatures = filteredFeatures.filter((feature) => feature.published === published);
		}

		// Filter status
		if (filter.status !== -1)
			filteredFeatures = filteredFeatures.filter((feature) => feature.status === filter.status);

		// Filter Features that I voted
		if (filter.voted) filteredFeatures = filteredFeatures.filter((feature) => feature.hasVoted === filter.voted);

		// Filter Features requested by me
		if (filter.requestedByMe)
			filteredFeatures = filteredFeatures.filter((feature) => feature.requestedBy.length === 1);

		// aplly filter based on search field
		if (filter.searchField !== "") {
			filteredFeatures = filteredFeatures.filter((feature) => {
				return (
					feature.title.toLowerCase().includes(filter.searchField.toLowerCase()) ||
					feature.description.toLowerCase().includes(filter.searchField.toLowerCase()) ||
					feature.idFeedback.toString().includes(filter.searchField.toLowerCase()) ||
					feature.requestedBy
						.map((user) => user.userName)
						.join()
						.includes(filter.searchField.toLowerCase())
				);
			});
		}

		// pass the features filtered to the parent to render
		onChange(filteredFeatures);
		setEstDevTimeFeaturesFiltered(
			Math.round(filteredFeatures.reduce((n, { estDevTimeInDays }) => n + estDevTimeInDays, 0))
		);

		saveFilterInLocalStorage(filter);

		// filter again if the filters changes or new features are created
	}, [filter, features, debouncedSearchField]);

	//handles
	// update filter if it is choosed in the dropdown menu
	const handleItemSelected = (item, type) => {
		setFilter({ ...filter, [type]: item.id });
	};

	const handleChangeCheckbox = (e) => {
		setFilter({ ...filter, [e.target.name]: e.target.checked });
	};

	// reset filters button
	const onClickResetFilters = () => {
		setFilter({
			module: -1,
			priority: -1,
			published: -1,
			status: -1,
			voted: false,
			requestedByMe: false,
			searchField: "",
		});
	};

	//search bar
	const handleSearchFilter = (e) => {
		setFilter({ ...filter, searchField: e.target.value });
	};

	const emptyItemModule = new dropDownOptions("Module", "", "", -1);
	const emptyItemPriority = new dropDownOptions("Priority", "", "", -1);
	const emptyItemPublication = new dropDownOptions("Visibility", "", "", -1);
	const emptyItemStatus = new dropDownOptions("Status", "", "", -1);
	// dropdown items imported
	const moduleItems = [emptyItemModule, ...dropDownModuleOptionsArray];
	const priorityItems = [emptyItemPriority, ...dropDownPriorityOptionsArray];
	const publicationItems = [emptyItemPublication, ...dropDownPublicationOptionsArray];
	const statusItems = [
		emptyItemStatus,
		dropDownStatusOptionsArray[Status.Open],
		dropDownStatusOptionsArray[Status.UnderReview],
		dropDownStatusOptionsArray[Status.Closed],
	];

	const formatValue = (value) => value.toFixed(0);
	return (
		<>
			<div className={styles.filters}>
				<AnimatedNumber
					value={filteredFeaturesLength}
					formatValue={formatValue}
					duration={300}
					className={styles.filteredFeaturesLength}
				/>
				{role === Role.Admin && <div className={styles.totalDevDays}>{estDevTimeFeaturesFiltered}d</div>}

				<div className={styles.group}>
					<DropDownMenu
						items={moduleItems}
						selectedItem={moduleItems[filter.module + 1]}
						onSelect={handleItemSelected}
						type="module"
						selectedItemWithLabel={true}
					/>
				</div>
				{role === Role.User && (
					<>
						<FormControlLabel
							name="voted"
							control={<Checkbox checked={filter.voted} onChange={handleChangeCheckbox} />}
							label="My votes"
						/>
						<FormControlLabel
							name="requestedByMe"
							control={<Checkbox checked={filter.requestedByMe} onChange={handleChangeCheckbox} />}
							label="My requests"
						/>
					</>
				)}
				{role === Role.Admin && (
					<>
						<div className={styles.group}>
							<DropDownMenu
								items={priorityItems}
								selectedItem={priorityItems[filter.priority + 1]}
								onSelect={handleItemSelected}
								type="priority"
							/>
						</div>
						<div className={styles.group}>
							<DropDownMenu
								items={publicationItems}
								selectedItem={publicationItems[filter.published + 1]}
								onSelect={handleItemSelected}
								type="published"
							/>
						</div>
						<div className={styles.group}>
							<DropDownMenu
								items={statusItems}
								selectedItem={statusItems.find((e) => e.id === filter.status)}
								onSelect={handleItemSelected}
								type="status"
							/>
						</div>
					</>
				)}
				<Button variant="outlined" onClick={onClickResetFilters}>
					<Icon path={mdiFilterOff} size={1} />
				</Button>
				<TextField
					variant="standard"
					placeholder="Search Features"
					name="searchField"
					value={filter.searchField}
					onChange={handleSearchFilter}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon path={mdiMagnify} size={0.9} />
							</InputAdornment>
						),
					}}
				/>
			</div>
		</>
	);
}
